import { useLayoutEffect, useRef, useState } from "react";

/**
 * FitText
 * --------
 * Shrinks its own rendered size (via a CSS scale transform, not font-size
 * directly) just enough that its content fits on one line within
 * whatever width its parent actually gives it, instead of wrapping or
 * being clipped. Built for admin-editable text whose length isn't known
 * ahead of time (the site's own business name in the navbar, currently
 * its only use) - a short name renders at full size; a long one shrinks
 * exactly as much as it needs to and no more, so "whatever the admin
 * types" always visibly appears rather than being cut off or forcing a
 * layout collision with its siblings.
 *
 * A scale transform rather than mutating font-size directly: line-height
 * and vertical alignment stay stable as the effective size changes, and
 * every measurement is taken against the text's own natural (unscaled)
 * width, so it's never path-dependent on whatever the previous scale
 * happened to be.
 *
 * Requires the parent to actually constrain this component's width (e.g.
 * a flex item with `min-w-0` so it can shrink below its content's
 * intrinsic size) - FitText only ever shrinks to fit whatever space it's
 * given, it doesn't create that constraint itself.
 *
 * Deliberately has no minimum-scale floor: a floor would mean a long
 * enough name gets clamped and then clipped by the overflow-hidden outer
 * span instead of shown in full, which is exactly the "even a full line
 * should appear" behaviour this component exists to guarantee. However
 * small the available space, the text keeps shrinking to fit it exactly.
 */
export default function FitText({ children, className = "" }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    const fit = () => {
      const available = outer.clientWidth;
      const natural = inner.scrollWidth;
      if (available <= 0 || natural <= 0) return;
      setScale(Math.min(1, available / natural));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(outer);
    // Also re-measure if the text content itself changes (an admin
    // editing the business name doesn't remount this component, and a
    // late-loading webfont can change the natural width after first
    // paint too).
    const mo = new MutationObserver(fit);
    mo.observe(inner, { characterData: true, childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <span ref={outerRef} className={`inline-block min-w-0 max-w-full overflow-hidden align-bottom ${className}`}>
      <span
        ref={innerRef}
        className="inline-block whitespace-nowrap"
        style={{ transform: `scale(${scale})`, transformOrigin: "left center" }}
      >
        {children}
      </span>
    </span>
  );
}
